import { useBodyClass } from "@portal/hooks/useBodyClass";
import { type ReactNode, useId, useState } from "react";
import FocusLock from "react-focus-lock";
import { cn } from "../../lib/utils";
import { Button, Card, Icon } from "../../primitives";
import { WorkspaceTab, type WorkspaceWrapperTab } from "./WorkspaceTab";

export type WorkspaceWrapperProps<TId extends string = string> = {
	tabs: readonly WorkspaceWrapperTab<TId>[];
	activeTabId: NoInfer<TId>;
	onTabChange: (tabId: NoInfer<TId>) => void;
	/** Accessible name for the tab strip (`aria-label` on `role="tablist"`). */
	tabListLabel: string;
	children: ReactNode;
	className?: string;
	testId?: string;
	/** Prefix for the tabs' (`-tab-${id}`) and the full-screen toggle's (`-full-screen`) tracking ids. */
	trackingId?: string;
};

export function WorkspaceWrapper<TId extends string = string>({
	tabs,
	activeTabId,
	onTabChange,
	tabListLabel,
	children,
	className,
	testId = "workspace-wrapper",
	trackingId,
}: WorkspaceWrapperProps<TId>) {
	const [showFullScreen, setShowFullScreen] = useState(false);
	const panelId = useId();
	const activeTab = tabs.find((tab) => tab.id === activeTabId);

	// An `isolate` ancestor caps the overlay's z-index; layouts read this class to lift that ancestor.
	useBodyClass("workspace-full-screen", showFullScreen);

	return (
		<Card
			elevation="raised"
			className={cn(
				"flex-1 min-h-0 h-full rounded-xl flex flex-col overflow-hidden",
				className,
				showFullScreen && "z-[200000]",
			)}
			data-testid={testId}
		>
			<Card.Body className="flex-1 min-h-0 p-0">
				{showFullScreen && (
					<div
						aria-hidden
						data-testid={`${testId}-full-screen-backdrop`}
						onMouseDown={() => setShowFullScreen(false)}
						className="fixed inset-0 bg-surface-darker/40"
					/>
				)}
				<FocusLock
					disabled={!showFullScreen}
					className={cn(
						"h-full flex flex-col",
						showFullScreen &&
							"fixed top-[1vh] left-[0.5vw] w-[99vw] h-[98vh] bg-surface-base rounded-lg overflow-hidden",
					)}
				>
					<div className="shrink-0 flex items-center justify-between px-4 border-b border-bc-default">
						<div className="flex gap-1" role="tablist" aria-label={tabListLabel}>
							{tabs.map((tab) => (
								<WorkspaceTab
									key={tab.id}
									tab={tab}
									isActive={activeTabId === tab.id}
									panelId={panelId}
									testId={testId}
									trackingId={trackingId}
									onClick={onTabChange}
								/>
							))}
						</div>
						<Button
							variant="outline"
							color="secondary"
							size="sm"
							onClick={() => setShowFullScreen(!showFullScreen)}
							data-testid={`${testId}-full-screen`}
							trackingId={trackingId ? `${trackingId}-full-screen` : undefined}
							leadingIcon={
								<Icon
									type={showFullScreen ? "arrows-pointing-in" : "arrows-pointing-out"}
									color="secondary"
									size="micro"
								/>
							}
						>
							{showFullScreen ? "Exit full screen" : "Full screen"}
						</Button>
					</div>
					<div
						role="tabpanel"
						id={panelId}
						aria-labelledby={activeTab != null ? `${testId}-tab-${activeTab.id}` : undefined}
						className="flex-1 flex flex-col"
						data-amp-mask
						data-sentry-mask
					>
						{children}
					</div>
				</FocusLock>
			</Card.Body>
		</Card>
	);
}
